import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Spinner,
  Table,
} from "../../../design-system";
import { Icon } from "../../../design-system";
import {
  getContractTerms,
  getEmployeeActiveContract,
  getEmployeeContracts,
  getHolidayGroups,
  getVacationPolicies,
  getWorkSchedules,
} from "../../../helpers/kronaly_helper";
import {
  CHANGE_TYPE_LABEL,
  formatDate,
  formatMinutesShort,
  formatMoney,
} from "./formatters";
import NewContractModal from "./NewContractModal";
import EditContractModal from "./EditContractModal";
import AddTermsModal from "./AddTermsModal";
import TerminateContractModal from "./TerminateContractModal";

const indexById = (arr) => {
  const map = {};
  for (const item of arr || []) {
    if (item?.id) map[item.id] = item;
  }
  return map;
};

const headerStyle = {
  padding: "12px 16px",
  textAlign: "left",
  color: "var(--kr-fg-muted)",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 600,
  borderBottom: "1px solid var(--kr-border-subtle)",
  whiteSpace: "nowrap",
};
const cellStyle = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  color: "var(--kr-fg-strong)",
  fontSize: 13,
};

function ContractRow({ contract, isActive, catalogs, onEdit, onAddTerms, onTerminate }) {
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTerms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getContractTerms(contract.id);
      setTerms(r?.data ?? r ?? []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  }, [contract.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && terms === null) loadTerms();
  };

  const isOpenContract = !contract.end_date;

  return (
    <>
      <tr>
        <td style={cellStyle}>{formatDate(contract.start_date)}</td>
        <td style={cellStyle}>
          {contract.end_date ? (
            formatDate(contract.end_date)
          ) : (
            <span className="kr-text-muted">Indefinido</span>
          )}
        </td>
        <td style={cellStyle}>
          {isActive ? (
            <Badge tone="success">Vigente</Badge>
          ) : isOpenContract ? (
            <Badge tone="info">Abierto</Badge>
          ) : (
            <Badge tone="neutral">Cerrado</Badge>
          )}
        </td>
        <td style={{ ...cellStyle, textAlign: "right" }}>
          <Button variant="secondary" size="sm" onClick={toggle} style={{ marginRight: 8 }}>
            <Icon name={open ? "Minus" : "Plus"} size={14} />
            <span style={{ marginLeft: 4 }}>Historial</span>
          </Button>
          <Button variant="soft" size="sm" onClick={() => onEdit(contract)} style={{ marginRight: 8 }}>
            <Icon name="Settings" size={14} />
            <span style={{ marginLeft: 4 }}>Editar</span>
          </Button>
          {isOpenContract && (
            <>
              <Button variant="soft" size="sm" onClick={() => onAddTerms(contract)} style={{ marginRight: 8 }}>
                <Icon name="Pencil" size={14} />
                <span style={{ marginLeft: 4 }}>Anexar</span>
              </Button>
              <Button variant="dangerSoft" size="sm" onClick={() => onTerminate(contract)}>
                <Icon name="XCircle" size={14} />
                <span style={{ marginLeft: 4 }}>Terminar</span>
              </Button>
            </>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} style={{ padding: 0 }}>
            <div style={{ background: "var(--kr-bg-subtle)", padding: 16 }}>
              {loading ? (
                <div className="kr-text-center"><Spinner size={16} /></div>
              ) : error ? (
                <Alert tone="danger">{error}</Alert>
              ) : !terms || terms.length === 0 ? (
                <p className="kr-text-muted kr-fs-13 kr-mb-0">Sin terms registrados.</p>
              ) : (
                <Table dense>
                  <thead>
                    <tr>
                      <th style={headerStyle}>Vigencia</th>
                      <th style={headerStyle}>Tipo</th>
                      <th style={headerStyle}>Puesto</th>
                      <th style={headerStyle}>Salario</th>
                      <th style={headerStyle}>Horario</th>
                      <th style={headerStyle}>Festivos</th>
                      <th style={headerStyle}>Vacaciones</th>
                      <th style={headerStyle}>Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terms.map((t) => {
                      const ws = catalogs.schedules[t.work_schedule_id];
                      const hgIds = Array.isArray(t.holiday_group_ids) ? t.holiday_group_ids : [];
                      const hgNames = hgIds
                        .map((id) => catalogs.holidays[id]?.name)
                        .filter(Boolean)
                        .join(" · ");
                      const vp = catalogs.vacations[t.vacation_policy_id];
                      return (
                        <tr key={t.id}>
                          <td style={cellStyle}>
                            {formatDate(t.effective_from)} →{" "}
                            {t.effective_to ? formatDate(t.effective_to) : <span className="kr-text-muted">vigente</span>}
                          </td>
                          <td style={cellStyle}>
                            {CHANGE_TYPE_LABEL[t.change_type] || t.change_type || "—"}
                          </td>
                          <td style={cellStyle}>{t.job_title || "—"}</td>
                          <td style={cellStyle}>{formatMoney(t.salary_amount, t.salary_currency)}</td>
                          <td style={cellStyle}>
                            {ws ? (
                              <span>
                                {ws.name}{" "}
                                <span className="kr-text-muted kr-fs-11">
                                  ({formatMinutesShort(ws.weekly_minutes)})
                                </span>
                              </span>
                            ) : (
                              <span className="kr-text-muted">—</span>
                            )}
                          </td>
                          <td style={cellStyle}>{hgNames || "—"}</td>
                          <td style={cellStyle}>{vp?.name || "—"}</td>
                          <td style={cellStyle}>{t.note || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ActiveContractCard({ active, catalogs }) {
  if (!active?.contract) {
    return (
      <Card style={{ marginBottom: 12 }}>
        <h6 className="kr-text-muted kr-fs-12 kr-mb-1">Contrato vigente</h6>
        <p className="kr-mb-0">Sin contrato activo a la fecha actual.</p>
      </Card>
    );
  }
  const c = active.contract;
  const t = active.current_terms;
  const ws = t ? catalogs.schedules[t.work_schedule_id] : null;
  const hgIds = t && Array.isArray(t.holiday_group_ids) ? t.holiday_group_ids : [];
  const hgNames = hgIds.map((id) => catalogs.holidays[id]?.name).filter(Boolean).join(" · ");
  const vp = t ? catalogs.vacations[t.vacation_policy_id] : null;
  return (
    <Card padded={false} style={{ marginBottom: 12, border: "1px solid var(--kr-success-100)" }}>
      <div
        style={{
          padding: "10px 16px",
          background: "var(--kr-status-success-bg)",
          color: "var(--kr-status-success-fg)",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderBottom: "1px solid var(--kr-success-100)",
          fontSize: 13,
        }}
      >
        <Icon name="ShieldCheck" size={16} />
        Contrato vigente
      </div>
      <div className="kr-grid kr-gap-3" style={{ padding: 20, gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Vigencia</div>
          <span className="kr-fw-medium">
            {formatDate(c.start_date)} →{" "}
            {c.end_date ? formatDate(c.end_date) : "Indefinido"}
          </span>
        </div>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Puesto</div>
          <span className="kr-fw-medium">{t?.job_title || "—"}</span>
        </div>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Salario</div>
          <span className="kr-fw-medium">{formatMoney(t?.salary_amount, t?.salary_currency)}</span>
        </div>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Horario</div>
          <span className="kr-fw-medium">
            {ws ? `${ws.name} (${formatMinutesShort(ws.weekly_minutes)})` : "—"}
          </span>
        </div>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Grupos de festivos</div>
          <span className="kr-fw-medium">{hgNames || "—"}</span>
        </div>
        <div>
          <div className="kr-text-muted kr-fs-11" style={{ marginBottom: 2 }}>Política de vacaciones</div>
          <span className="kr-fw-medium">{vp?.name || "—"}</span>
        </div>
      </div>
    </Card>
  );
}

const TabContratos = ({ employeeProfileId }) => {
  const [contracts, setContracts] = useState([]);
  const [active, setActive] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(null);
  const [openTerms, setOpenTerms] = useState(null);
  const [openTerminate, setOpenTerminate] = useState(null);

  const load = useCallback(async () => {
    if (!employeeProfileId) return;
    setLoading(true);
    setError(null);
    try {
      const [list, act, ws, hg, vp] = await Promise.all([
        getEmployeeContracts(employeeProfileId),
        getEmployeeActiveContract(employeeProfileId),
        getWorkSchedules(),
        getHolidayGroups(),
        getVacationPolicies(),
      ]);
      setContracts(list?.data ?? list ?? []);
      setActive(act?.data ?? act ?? null);
      setSchedules(ws?.data ?? ws ?? []);
      setHolidays(hg?.data ?? hg ?? []);
      setVacations(vp?.data ?? vp ?? []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los contratos.");
    } finally {
      setLoading(false);
    }
  }, [employeeProfileId]);

  useEffect(() => { load(); }, [load]);

  const catalogs = useMemo(
    () => ({
      schedules: indexById(schedules),
      holidays: indexById(holidays),
      vacations: indexById(vacations),
    }),
    [schedules, holidays, vacations],
  );

  const activeContractId = active?.contract?.id;

  return (
    <Card padded={false}>
      <div
        className="kr-flex kr-items-center kr-justify-between"
        style={{ padding: "14px 20px", borderBottom: "1px solid var(--kr-border-subtle)" }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
          Contratos
        </h3>
        <Button variant="primary" size="sm" icon="Plus" onClick={() => setOpenNew(true)}>
          Nuevo contrato
        </Button>
      </div>
      <div style={{ padding: 20 }}>
        {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}
        {loading ? (
          <div className="kr-text-center kr-py-5"><Spinner size={20} /></div>
        ) : (
          <>
            <ActiveContractCard active={active} catalogs={catalogs} />
            {contracts.length === 0 ? (
              <Alert tone="info">
                Este empleado todavía no tiene contratos. Crea el primero para definir
                horario, festivos y política de vacaciones.
              </Alert>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th style={headerStyle}>Inicio</th>
                    <th style={headerStyle}>Fin</th>
                    <th style={headerStyle}>Estado</th>
                    <th style={{ ...headerStyle, textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <ContractRow
                      key={c.id}
                      contract={c}
                      isActive={c.id === activeContractId}
                      catalogs={catalogs}
                      onEdit={(ct) => setOpenEdit(ct)}
                      onAddTerms={(ct) => setOpenTerms(ct)}
                      onTerminate={(ct) => setOpenTerminate(ct)}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </div>

      <NewContractModal
        open={openNew}
        employeeProfileId={employeeProfileId}
        onClose={() => setOpenNew(false)}
        onCreated={load}
      />
      <EditContractModal
        open={!!openEdit}
        contract={openEdit}
        onClose={() => setOpenEdit(null)}
        onUpdated={load}
      />
      <AddTermsModal
        open={!!openTerms}
        contractId={openTerms?.id}
        onClose={() => setOpenTerms(null)}
        onCreated={load}
      />
      <TerminateContractModal
        open={!!openTerminate}
        contract={openTerminate}
        onClose={() => setOpenTerminate(null)}
        onTerminated={load}
      />
    </Card>
  );
};

export default TabContratos;
